const createOrder = async ({
  currencyCode,
  amount,
  intent,
  enableShippingOptions,
  enableServerSideShippingCallback,
  csrfToken,
  updateLogs,
  clientId,
  product = 'standard',
  paymentSource,
  threeDsSetting,
  appIntegrationType,
  sellerId,
  appSwitch,
  contactData,
  contactPreference,
  buyerEmailAddress
}) => {
  const payload = {
    clientId,
    currencyCode,
    amount,
    intent: intent ? intent.toUpperCase() : null,
    enableShippingOptions,
    product,
    paymentSource,
    threeDsSetting,
    appIntegrationType,
    sellerId,
    appSwitch,
    enableServerSideShippingCallback,
    contactPreference,
    contactData,
    buyerEmailAddress
  };
  let request;
  let response;
  let log;
  try {
    const apiResponse = await fetch('/studio/api/v1/order/create', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    ({ request, response } = await apiResponse.json());
    return response.id;
  } catch (err) {
    response = err;
    return err;
  } finally {
    log = {
      name: 'Create Order',
      timestamp: new Date().toLocaleString(),
      url: 'POST to v2/checkout/orders',
      request,
      response
    };
    updateLogs(log);
  }
};

const captureOrder = async ({
  orderId,
  csrfToken,
  updateLogs,
  updateLastCapture,
  clientId,
  appIntegrationType,
  sellerId
}) => {
  let response;
  let log;
  try {
    const apiResponse = await fetch(`/studio/api/v1/orders/${orderId}/capture`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        clientId,
        appIntegrationType,
        sellerId
      })
    });

    response = await apiResponse.json();
    delete response.sys;
    const captureIds = response.purchase_units
      ?.map((purchaseUnit) => purchaseUnit.payments.captures?.map((capture) => capture.id))
      .flat();
    updateLastCapture(captureIds);
  } catch (err) {
    response = err;
  } finally {
    log = {
      name: 'Capture Order',
      timestamp: new Date().toLocaleString(),
      url: `POST to v2/checkout/orders/${orderId}/capture`,
      request: {},
      response
    };
    updateLogs(log);
  }
};

const authorizeOrder = async ({
  orderId,
  csrfToken,
  updateLogs,
  clientId,
  appIntegrationType,
  sellerId
}) => {
  let response;
  let authorizationId;
  let log;
  try {
    const apiResponse = await fetch(`/studio/api/v1/orders/${orderId}/authorize`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        clientId,
        appIntegrationType,
        sellerId
      })
    });

    response = await apiResponse.json();
    authorizationId = response?.purchase_units?.[0]?.payments?.authorizations[0].id;
    delete response.sys;
  } catch (err) {
    response = err;
  } finally {
    log = {
      name: 'Authorize Order',
      timestamp: new Date().toLocaleString(),
      url: `POST to v2/checkout/orders/${orderId}/authorize`,
      request: {},
      response
    };

    updateLogs(log);
  }
  return authorizationId;
};

const captureAuthorize = async ({
  authorizationId,
  csrfToken,
  updateLogs,
  updateLastCapture,
  clientId,
  appIntegrationType,
  sellerId
}) => {
  let response;
  let log;
  try {
    const apiResponse = await fetch(`/studio/api/v1/orders/${authorizationId}/captureAuthorize`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        clientId,
        appIntegrationType,
        sellerId
      })
    });

    response = await apiResponse.json();
    delete response.sys;
  } catch (err) {
    response = err;
  } finally {
    log = {
      name: 'Capture Authorize',
      timestamp: new Date().toLocaleString(),
      url: `POST to v2/payments/authorizations/${authorizationId}/capture`,
      request: {},
      response
    };
    updateLastCapture([response.id]);
    updateLogs(log);
  }
};

const shippingAddressChange = (data, updateLogs) => {
  const log = {
    name: 'Shipping Address Change',
    timestamp: new Date().toLocaleString(),
    callback: data?.shippingAddress
  };

  updateLogs(log);
};

const shippingOptionsChange = (data, updateLogs) => {
  const log = {
    name: 'Shipping Options Change',
    timestamp: new Date().toLocaleString(),
    callback: data?.selectedShippingOption
  };

  updateLogs(log);
};

const shippingServerSideCallback = (data, updateLogs) => {
  const log = {
    name: 'Server Side Shipping Callback',
    url: 'POST to /studio/api/v1/callback',
    timestamp: new Date().toLocaleString(),
    id: data?.id,
    request: data?.request,
    response: data?.response
  };

  updateLogs(log);
};

const refundCapture = async ({
  captureIds,
  csrfToken,
  updateLogs,
  toggleTestPanel,
  updateLastCapture,
  clientId,
  appIntegrationType,
  sellerId
}) => {
  let responses;
  let log;
  try {
    const refundRequests = captureIds.map((id) =>
      fetch(`/studio/api/v1/payments/captures/${id}/refund`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientId,
          appIntegrationType,
          sellerId
        })
      })
    );

    responses = await Promise.all(refundRequests);
    responses = await Promise.all(responses.map((r) => r.json()));
  } catch (err) {
    responses = [err];
  } finally {
    responses.forEach((response, index) => {
      const captureId = captureIds[index];
      delete response.sys;
      log = {
        name: 'Refund Capture',
        timestamp: new Date().toLocaleString(),
        url: `POST to v2/payments/captures/${captureId}/refund`,
        request: {},
        response
      };
      updateLogs(log);
      updateLastCapture(null);
      toggleTestPanel(true);
    });
  }
};

const voidOrder = async ({ orderId, csrfToken, updateLogs, clientId }) => {
  let response;
  let log;
  try {
    const apiResponse = await fetch(`/studio/api/v1/orders/${orderId}/void`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        clientId
      })
    });

    response = await apiResponse.json();
    delete response.sys;
  } catch (err) {
    response = err;
  } finally {
    log = {
      name: 'Void Order',
      timestamp: new Date().toLocaleString(),
      url: `POST to v2/checkout/orders/${orderId}/void`,
      request: {},
      response
    };
    updateLogs(log);
  }
};

const logConfirmPaymentSource = ({ updateLogs, errorMsg }) => {
  const log = {
    name: 'SDK Error',
    url: 'Error',
    timestamp: new Date().toLocaleString(),
    error: errorMsg
  };

  updateLogs(log);
};

const setupTokens = async ({ csrfToken, clientId, updateLogs, vaultTokenOptions }) => {
  let request;
  let response;
  let log;
  const payload = {
    clientId,
    vaultTokenOptions
  };
  try {
    const apiResponse = await fetch('/studio/api/v1/vault/setup-tokens', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    ({ request, response } = await apiResponse.json());
    return response.id;
  } catch (err) {
    response = err;
    return err;
  } finally {
    log = {
      name: 'Setup Tokens',
      timestamp: new Date().toLocaleString(),
      url: 'POST to v3/vault/setup-tokens',
      request,
      response
    };
    updateLogs(log);
  }
};

const createPaymentToken = async ({
  vaultSetupToken,
  csrfToken,
  updateLogs,
  clientId,
  addPaymentToken
}) => {
  let request;
  let response;
  let log;
  const payload = {
    clientId,
    vaultSetupToken
  };
  try {
    const apiResponse = await fetch('/studio/api/v1/vault/payment-tokens', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    ({ request, response } = await apiResponse.json());
    if (response && response.id) {
      addPaymentToken(response.id);
      return response.id;
    }
  } catch (err) {
    response = err;
    return err;
  } finally {
    log = {
      name: 'Payment Tokens',
      timestamp: new Date().toLocaleString(),
      url: 'POST to v3/vault/payment-tokens',
      request,
      response
    };
    updateLogs(log);
  }
};

export {
  createOrder,
  captureOrder,
  authorizeOrder,
  shippingAddressChange,
  shippingOptionsChange,
  refundCapture,
  voidOrder,
  captureAuthorize,
  logConfirmPaymentSource,
  setupTokens,
  createPaymentToken,
  shippingServerSideCallback
};
